// =============================================================================
// Hosted-agent eval suite — non-component helpers (#3028)
//
// The one rule this module exists to protect: the contract is THREE-VALUED.
// "passed" / "failed" / "indeterminate" are three different statements, and
// `indeterminate` (plus its case-level cousins `errored` and `skipped`) means
// *we could not measure this* — never *the agent got it wrong*. Every helper
// below keeps two axes apart:
//
//   axis 1 — was it measured?   measured | unmeasured
//   axis 2 — did it hold?       passed   | failed      (only for measured)
//
// Red and green belong to axis 2 alone. Unmeasured outcomes get amber/grey
// plus a dashed border, so "no measurement" stays readable without colour
// vision and can never be mistaken for a verdict.
//
// Wire types, caps and the kind vocabulary live in
// `api/admin/hosted-agents-evals.ts`; this module never re-declares them. The
// case-editor half — drafts, payload building and client-side validation —
// lives in `hosted-agent-eval-cases.ts`.
// =============================================================================

import type {
  HostedAgentEvalCaseStatus,
  HostedAgentEvalExpectationKind,
  HostedAgentEvalRun,
  HostedAgentEvalRunStatus,
  HostedAgentEvalRunTotals,
} from "@/api/admin-api"
import type { BadgeProps } from "@/components/ui/badge"

// The poll cadence and its stop conditions live in
// `hosted-agent-eval-run-state.ts` — a running suite has to stop being polled
// for more reasons than "it finished".

/** `expected` values accepted by the `run_status` kind. */
export const HOSTED_AGENT_EVAL_RUN_STATUS_VALUES = ["completed", "failed"] as const

// ---------------------------------------------------------------------------
// Which fields each kind actually uses (mirrors `_check_kind_fields`)
// ---------------------------------------------------------------------------

const TEXT_KINDS = new Set<string>(["answer_contains", "answer_not_contains"])
const TOOL_KINDS = new Set<string>(["tool_called", "tool_not_called", "tool_denied"])
const COUNT_KINDS = new Set<string>(["artifact_produced", "cited_source"])
const LIMIT_KINDS = new Set<string>(["max_cost_sek", "max_tokens", "max_duration_ms"])

/**
 * The server model is `extra="forbid"`, so the editor must send *only* the
 * fields the chosen kind uses — a stale `tool` left over from a kind the admin
 * switched away from is a 400, not a harmless no-op.
 */
export type HostedAgentEvalExpectationFields = {
  expected: boolean
  value: boolean
  caseSensitive: boolean
  tool: boolean
  action: boolean
  detailContains: boolean
  code: boolean
  minCount: boolean
  limit: boolean
}

export function hostedAgentEvalExpectationFields(
  kind: HostedAgentEvalExpectationKind
): HostedAgentEvalExpectationFields {
  return {
    expected: kind === "run_status",
    value: TEXT_KINDS.has(kind),
    caseSensitive: TEXT_KINDS.has(kind),
    tool: TOOL_KINDS.has(kind),
    // `action` narrows a tool grant; `tool_denied` matches on `code` instead.
    action: kind === "tool_called" || kind === "tool_not_called",
    // `detailContains` matches the ledger's argument summary (script path,
    // command head) — how a skill asserts its OWN script ran.
    detailContains: kind === "tool_called" || kind === "tool_not_called",
    code: kind === "tool_denied",
    minCount: COUNT_KINDS.has(kind),
    limit: LIMIT_KINDS.has(kind),
  }
}

// ---------------------------------------------------------------------------
// Detail codes — rendered through OWN i18n keys, never from server prose
// ---------------------------------------------------------------------------

/**
 * English fallbacks used as i18n `defaultValue`. An unknown code falls back to
 * the raw code in `font-mono` — never to the server's `detail` field, which is
 * English-only prose kept for logs and is not a rendering contract.
 */
export const HOSTED_AGENT_EVAL_DETAIL_DEFAULTS: Record<string, string> = {
  no_observation: "The run produced no observation, so nothing could be checked.",
  no_tool_ledger: "No tool-call ledger was recorded, so tool use could not be read.",
  tool_ledger_truncated: "The tool-call ledger was truncated — tool use could not be read in full.",
  tool_errored: "The tool call errored, so its result could not be judged.",
  duration_unavailable: "No duration was recorded, so the time limit could not be checked.",
  unsupported_kind: "The server does not support this expectation kind.",
}

export const HOSTED_AGENT_EVAL_CASE_DETAIL_DEFAULTS: Record<string, string> = {
  run_not_observed: "The run started but was never observed reaching a verdict.",
  dispatch_failed: "The run could not be dispatched to the agent.",
  suite_aborted_agent_paused: "The suite stopped here: the agent was paused mid-run.",
  suite_aborted_cost_ceiling: "The suite stopped here: the cost ceiling was reached.",
}

export const HOSTED_AGENT_EVAL_STATUS_DEFAULTS: Record<string, string> = {
  passed: "Passed",
  failed: "Failed",
  indeterminate: "Not measured",
  errored: "Errored",
  skipped: "Skipped",
  running: "Running",
  completed: "Completed",
}

export const HOSTED_AGENT_EVAL_KIND_DEFAULTS: Record<string, string> = {
  run_status: "Run status",
  answer_contains: "Answer contains",
  answer_not_contains: "Answer does not contain",
  tool_called: "Tool was called",
  tool_not_called: "Tool was not called",
  tool_denied: "Tool call was denied",
  artifact_produced: "Artifacts produced",
  cited_source: "Sources cited",
  max_cost_sek: "Cost at most",
  max_tokens: "Tokens at most",
  max_duration_ms: "Duration at most",
}

// ---------------------------------------------------------------------------
// Outcome → presentation. Two axes, never one.
// ---------------------------------------------------------------------------

const UNMEASURED_STATUSES = new Set<string>(["indeterminate", "errored", "skipped"])

/** True when the status says "we could not measure this", not "it failed". */
export function isHostedAgentEvalUnmeasured(status: string): boolean {
  return UNMEASURED_STATUSES.has(status)
}

/** Badge variant per outcome. Green/red only for the two measured verdicts. */
export function hostedAgentEvalOutcomeVariant(status: string): BadgeProps["variant"] {
  if (status === "passed") return "success"
  if (status === "failed") return "destructive"
  if (status === "skipped") return "disabled"
  // A suite still running has no verdict yet — that is a THIRD thing, distinct
  // from "we tried and could not measure it", and must not borrow amber.
  if (status === "running") return "secondary"
  // indeterminate / errored / anything unknown: tinted amber, never solid.
  return "warning"
}

/**
 * Row chrome per outcome. A measured row keeps a solid border; an unmeasured
 * one is dashed — "no measurement, no solid frame" — so the distinction
 * survives greyscale and colour blindness.
 */
export function hostedAgentEvalOutcomeRowClass(status: string): string {
  if (status === "skipped") return "border-dashed opacity-70"
  if (isHostedAgentEvalUnmeasured(status)) return "border-dashed"
  return ""
}

// ---------------------------------------------------------------------------
// Suite totals
// ---------------------------------------------------------------------------

export type HostedAgentEvalTotalsView = {
  cases: number
  passed: number
  failed: number
  /**
   * Every case that produced no verdict — the ones that *reported* an
   * unmeasurable outcome plus the ones that never reported at all. This is the
   * number the suite exists to keep visible, so it must never be read off the
   * reported buckets alone.
   */
  unmeasured: number
  indeterminate: number
  errored: number
  skipped: number
  /** indeterminate + errored + skipped — reported "we could not measure". */
  reportedUnmeasured: number
  /**
   * Cases in the suite that never reported anything. A run that crashes after
   * case 3 of 7 keeps its last progress snapshot, so the four missing cases
   * exist ONLY as this difference: counting the reported buckets would print
   * "0 could not be measured" over a suite that measured four of nothing.
   */
  unreported: number
  /** Backend score: passed / cases. Unmeasured cases sit in the denominator. */
  score: number
  /** (passed + failed) / cases — how much of the suite produced evidence. */
  coverage: number
  /** True when at least one case produced no evidence either way. */
  hasEvidenceGap: boolean
  /** The suite is still running, so missing cases are pending, not lost. */
  isRunning: boolean
  /** Nothing failed, but something could not be measured — not a clean pass. */
  isIncomplete: boolean
}

export function summarizeHostedAgentEvalTotals(
  totals: HostedAgentEvalRunTotals | undefined,
  status?: HostedAgentEvalRunStatus | string
): HostedAgentEvalTotalsView {
  const cases = Math.max(0, totals?.cases ?? 0)
  const passed = Math.max(0, totals?.passed ?? 0)
  const failed = Math.max(0, totals?.failed ?? 0)
  const indeterminate = Math.max(0, totals?.indeterminate ?? 0)
  const errored = Math.max(0, totals?.errored ?? 0)
  const skipped = Math.max(0, totals?.skipped ?? 0)
  const reportedUnmeasured = indeterminate + errored + skipped
  const measured = passed + failed
  const unreported = Math.max(0, cases - measured - reportedUnmeasured)
  const unmeasured = reportedUnmeasured + unreported
  const isRunning = status === "running"
  return {
    cases,
    passed,
    failed,
    unmeasured,
    indeterminate,
    errored,
    skipped,
    reportedUnmeasured,
    unreported,
    score: totals?.score ?? 0,
    coverage: cases > 0 ? measured / cases : 0,
    hasEvidenceGap: cases > 0 && measured < cases,
    isRunning,
    // While the suite runs the gap is simply "not there yet"; once it settles,
    // the same gap is the finding.
    isIncomplete: !isRunning && failed === 0 && unmeasured > 0,
  }
}

/** Segment widths (percent) for the three-part summary bar. */
export function hostedAgentEvalBarSegments(view: HostedAgentEvalTotalsView): {
  passed: number
  failed: number
  unmeasured: number
} {
  const total = view.cases || 1
  return {
    passed: (view.passed / total) * 100,
    failed: (view.failed / total) * 100,
    unmeasured: (view.unmeasured / total) * 100,
  }
}

/**
 * The diagonal hatch that marks the unmeasured segment. A hole in the evidence
 * has to *look* like a hole, not like a third coloured result.
 */
export const HOSTED_AGENT_EVAL_UNMEASURED_HATCH =
  "repeating-linear-gradient(45deg, hsl(var(--warning) / 0.55) 0 4px, hsl(var(--warning) / 0.18) 4px 8px)"

// ---------------------------------------------------------------------------
// Comparability
// ---------------------------------------------------------------------------

export type HostedAgentEvalComparability = {
  /** The run straddled two agent revisions — it cannot support "v4 beats v3". */
  notComparable: boolean
  /** Provenance could not be verified (agent unreadable) — a separate claim. */
  unverified: boolean
  /** The run covered only a subset of the cases. */
  partial: boolean
  revisionAtStart: number | null
  revisionAtFinish: number | null
}

export function hostedAgentEvalComparability(
  run: HostedAgentEvalRun | undefined
): HostedAgentEvalComparability {
  const provenance = run?.provenance
  const start = typeof provenance?.agent_revision === "number" ? provenance.agent_revision : null
  const finish =
    typeof provenance?.agent_revision_at_finish === "number"
      ? provenance.agent_revision_at_finish
      : null
  return {
    notComparable: provenance?.comparable === false,
    // `verified: false` + `comparable: true` means "we could not check" — NOT
    // "verified comparable". It must be surfaced on its own and must never be
    // silenced by a `comparable` flag that merely kept its optimistic default.
    unverified: provenance?.verified === false,
    partial: run?.partial === true,
    revisionAtStart: start,
    revisionAtFinish: finish,
  }
}

/** A run may only be offered for comparison when it is whole and comparable. */
export function canCompareHostedAgentEvalRun(run: HostedAgentEvalRun | undefined): boolean {
  if (!run || run.status !== "completed") return false
  const comparability = hostedAgentEvalComparability(run)
  return !comparability.notComparable && !comparability.partial
}

/**
 * Two runs sat the same exam only when `eval_cases_sha256` matches. A differing
 * hash means different test material — the history flags it rather than letting
 * two scores be read side by side.
 */
export function hostedAgentEvalSameExam(
  a: HostedAgentEvalRun | undefined,
  b: HostedAgentEvalRun | undefined
): boolean {
  const left = a?.provenance.eval_cases_sha256 ?? null
  const right = b?.provenance.eval_cases_sha256 ?? null
  if (!left || !right) return false
  return left === right
}

/** Newest terminal run older than `run` — the "same exam?" reference row. */
export function hostedAgentEvalPreviousRun(
  runs: readonly HostedAgentEvalRun[],
  run: HostedAgentEvalRun
): HostedAgentEvalRun | undefined {
  const index = runs.findIndex((candidate) => candidate.id === run.id)
  if (index < 0) return undefined
  return runs.slice(index + 1).find((candidate) => candidate.status !== "running")
}

// ---------------------------------------------------------------------------
// Result ordering and formatting
// ---------------------------------------------------------------------------

function caseStatusOrder(status: HostedAgentEvalCaseStatus | string): number {
  switch (status) {
    case "failed":
      return 0
    case "indeterminate":
      return 1
    case "errored":
      return 2
    case "skipped":
      return 3
    default:
      return 4
  }
}

/** Failures first, then everything that could not be measured, then passes. */
export function sortHostedAgentEvalCaseResults<T extends { status: HostedAgentEvalCaseStatus }>(
  results: readonly T[]
): T[] {
  return [...results].sort((a, b) => caseStatusOrder(a.status) - caseStatusOrder(b.status))
}

export function formatHostedAgentEvalPercent(fraction: number, locale?: string): string {
  const safe = Number.isFinite(fraction) ? fraction : 0
  try {
    return new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(
      safe
    )
  } catch {
    return `${Math.round(safe * 100)}%`
  }
}

/** Short sha for provenance rows: the first 12 hex chars, or a dash. */
export function shortHostedAgentEvalDigest(digest: string | null | undefined): string {
  if (!digest) return "—"
  if (digest === "unavailable") return digest
  return digest.slice(0, 12)
}
